import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Chip, Paper, Typography } from '@mui/material';

// 🧭 Швидкий перехід по розділах вкладки (06.10.2026, прохання OWNER: «не листати все меню
// вниз, шукаючи потрібний пункт»).
//
// Розділи НЕ перелічуються вручну — компонент сам знаходить блоки (MUI Paper верхнього рівня)
// ПІСЛЯ себе в тому ж контейнері й бере їхній заголовок. Тож працює в кожній вкладці й не
// застаріє, коли додадуть новий блок. Явна назва: data-qnav="…" на Paper або на заголовку.
//
// ⚠️ position:sticky тут не працює: <main> дашборду має overflow-x:hidden, а це робить його
// «контейнером прокрутки» для sticky. Тому вгорі — звичайний рядок кнопок, а коли він
// зникає з екрана, у куті зʼявляється кнопка «🧭 Розділи» з тим самим списком.

const MIN_SECTIONS = 3;

function titleOf(paper) {
  const explicit = paper.getAttribute('data-qnav') || (paper.querySelector('[data-qnav]') || {}).getAttribute?.('data-qnav');
  if (explicit) return explicit;
  const h = paper.querySelector('h1,h2,h3,h4,h5,h6');
  if (h && h.textContent.trim()) return h.textContent.trim();
  // перший жирний короткий напис — так оформлені заголовки на сторінках застосунків
  const els = paper.querySelectorAll('p,span,div');
  for (let i = 0, n = 0; i < els.length && n < 40; i++) {
    const el = els[i];
    if (el.closest('input,textarea,button,.MuiChip-root,.MuiTableCell-root')) continue;
    const t = (el.textContent || '').trim();
    if (t.length < 2 || t.length > 70 || t === '⠿') continue;
    if (el.children.length > 0 && el.firstElementChild && el.firstElementChild.textContent.trim() === t) continue;
    n++;
    const fw = parseInt(getComputedStyle(el).fontWeight, 10) || 400;
    if (fw >= 600) return t;
  }
  // запасне: перший короткий напис, що починається з емодзі (так підписані блоки NUMERIQ)
  for (let i = 0; i < els.length && i < 60; i++) {
    const el = els[i];
    if (el.closest('input,textarea,button,.MuiChip-root,.MuiTableCell-root')) continue;
    const t = (el.textContent || '').trim();
    if (t.length >= 2 && t.length <= 70 && /^\p{Extended_Pictographic}/u.test(t) && el.children.length === 0) return t;
  }
  return '';
}

export default function AppQuickNav({ order }) {
  const navRef = useRef(null);
  const [items, setItems] = useState([]);   // [{title, el}]
  const [active, setActive] = useState(-1);
  const [floatOn, setFloatOn] = useState(false);
  const [open, setOpen] = useState(false);
  const itemsRef = useRef([]);
  const sigRef = useRef('');

  const scan = useCallback(() => {
    const nav = navRef.current;
    const root = nav && nav.parentElement;
    if (!root) return;
    const found = [];
    root.querySelectorAll('.MuiPaper-root').forEach((p) => {
      if (p.classList.contains('MuiAlert-root') || p.closest('.MuiDialog-root,.MuiPopover-root,[data-qnav-skip]')) return;
      if (!(nav.compareDocumentPosition(p) & Node.DOCUMENT_POSITION_FOLLOWING)) return;   // лише блоки ПІСЛЯ панелі
      const outer = p.parentElement && p.parentElement.closest('.MuiPaper-root');
      if (outer && root.contains(outer)) return;                                            // вкладені — не розділи
      if (!p.getClientRects().length || getComputedStyle(p).display === 'none') return;      // інша вкладка / приховано
      if (p.offsetHeight < 60) return;                                                     // дрібні картки-цифри
      const t = titleOf(p);
      if (!t) return;
      found.push({ title: t, el: p, level: 0, y: p.getBoundingClientRect().top + window.scrollY });
      // Підрозділи всередині блоку: жирний короткий заголовок з емодзі, окремим рядком.
      // Саме вони рятують вкладки з ОДНИМ великим блоком (ZODIAQ «Консультації», NUMERIQ).
      let subs = 0;
      p.querySelectorAll('p,h4,h5,h6,span').forEach((el) => {
        if (subs >= 10 || el.children.length) return;
        if (el.closest('table,.MuiTableContainer-root,li,button,label,.MuiChip-root,[data-qnav-skip]')) return;
        const t2 = (el.textContent || '').trim();
        if (t2 === t || t2.length < 3 || t2.length > 60 || !/^\p{Extended_Pictographic}/u.test(t2)) return;
        if (/:\s*\d|\d{2,}/.test(t2)) return;   // «📊 Намальовано: 34 фото» — це цифра, а не розділ («⚡ Рівень 1» — розділ)
        // той самий розділ іншими словами («👥 Хто кого привів» усередині «👥 Реферали — хто кого привів»)
        const bare = (x) => x.replace(/^[^\p{L}]+/u, '').toLowerCase().trim();
        if (bare(t).includes(bare(t2)) || bare(t2).includes(bare(t))) return;
        if ((parseInt(getComputedStyle(el).fontWeight, 10) || 400) < 600 || !el.getClientRects().length) return;
        subs++;
        found.push({ title: t2, el, level: 1, y: el.getBoundingClientRect().top + window.scrollY });
      });
    });
    // у ZODIAQ блоки переставляються через CSS order — сортуємо за ВИДИМИМ положенням
    found.sort((a, b) => a.y - b.y);
    const seen = new Set();
    const uniq = found.filter((f) => (seen.has(f.title) ? false : (seen.add(f.title), true)));
    const sig = uniq.map((u) => u.level + u.title).join('\u0001');
    itemsRef.current = uniq;
    if (sig !== sigRef.current) { sigRef.current = sig; setItems(uniq); }
  }, []);

  useEffect(() => {
    const nav = navRef.current;
    const root = nav && nav.parentElement;
    if (!root) return undefined;
    let t = null;
    const later = () => { clearTimeout(t); t = setTimeout(scan, 350); };
    scan();
    const mo = new MutationObserver(later);
    mo.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
    window.addEventListener('resize', later);
    return () => { mo.disconnect(); window.removeEventListener('resize', later); clearTimeout(t); };
  }, [scan]);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const nav = navRef.current;
        if (nav) setFloatOn(nav.getBoundingClientRect().bottom < 0);
        let a = -1;
        itemsRef.current.forEach((it, i) => { if (it.el.getBoundingClientRect().top <= 140) a = i; });
        setActive(a);
      });
    };
    document.addEventListener('scroll', onScroll, true);
    onScroll();
    return () => { document.removeEventListener('scroll', onScroll, true); cancelAnimationFrame(raf); };
  }, [items]);

  const go = (el) => {
    setOpen(false);
    if (!el) return;
    el.style.scrollMarginTop = '24px';
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const toTop = () => { setOpen(false); const nav = navRef.current; if (nav) { nav.style.scrollMarginTop = '80px'; nav.scrollIntoView({ behavior: 'smooth', block: 'start' }); } };

  const show = items.length >= MIN_SECTIONS;
  const short = (s) => (s.length > 34 ? s.slice(0, 32) + '…' : s);
  return (
    <Box ref={navRef} data-qnav-skip="" sx={{ order: order, mb: show ? 2 : 0 }}>
      {show && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: .8, overflowX: 'auto', pb: .5,
          scrollbarWidth: 'thin', '&::-webkit-scrollbar': { height: 4 } }}>
          <Typography sx={{ color: '#8a83b0', fontSize: 12, whiteSpace: 'nowrap', mr: .3 }}>🧭 Перейти до:</Typography>
          {items.map((it, i) => (
            <Chip key={it.title} label={(it.level ? '› ' : '') + short(it.title)} title={it.title} onClick={() => go(it.el)} size="small"
              sx={{ flex: 'none', cursor: 'pointer', fontSize: it.level ? 12 : 12.5, opacity: it.level && active !== i ? .85 : 1,
                bgcolor: active === i ? 'rgba(255,212,121,.18)' : 'rgba(124,92,255,.12)',
                color: active === i ? '#ffd479' : '#cbb8ff',
                border: active === i ? '1px solid rgba(255,212,121,.6)' : '1px solid transparent' }} />
          ))}
        </Box>
      )}
      {show && floatOn && (
        <Box sx={{ position: 'fixed', right: { xs: 12, sm: 24 }, bottom: { xs: 12, sm: 24 }, zIndex: 1250,
          display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
          {open && (
            <Paper data-qnav-skip="" elevation={8} sx={{ p: 1, maxHeight: '60vh', overflowY: 'auto', width: 260, maxWidth: 'calc(100vw - 24px)',
              bgcolor: '#1a1530', border: '1px solid rgba(150,130,255,.3)', borderRadius: 2 }}>
              <Box onClick={toTop} sx={{ px: 1.2, py: .8, borderRadius: 1, cursor: 'pointer', color: '#22d3ee', fontSize: 13.5,
                '&:hover': { bgcolor: 'rgba(124,92,255,.15)' } }}>⬆ Вгору (вкладки)</Box>
              {items.map((it, i) => (
                <Box key={it.title} onClick={() => go(it.el)} sx={{ pl: it.level ? 3 : 1.2, pr: 1.2, py: it.level ? .55 : .8, borderRadius: 1, cursor: 'pointer', fontSize: it.level ? 12.5 : 13.5,
                  color: active === i ? '#ffd479' : '#ddd', bgcolor: active === i ? 'rgba(255,212,121,.1)' : 'transparent',
                  '&:hover': { bgcolor: 'rgba(124,92,255,.15)' } }}>{it.title}</Box>
              ))}
            </Paper>
          )}
          <Box onClick={() => setOpen(!open)} role="button" aria-label="Розділи сторінки"
            sx={{ px: 2, py: 1.1, borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 14, userSelect: 'none',
              background: 'linear-gradient(120deg,#ffd479,#22d3ee)', color: '#07070f', boxShadow: '0 4px 18px rgba(0,0,0,.45)' }}>
            {open ? '✕ Закрити' : '🧭 Розділи'}
          </Box>
        </Box>
      )}
    </Box>
  );
}
